import { loadPyodide } from 'pyodide'
import { freshMainGlobals } from '../../pyodideMain'
import { readPythonResult, upgradeLegacyPythonHarness } from '../../testResults'

// Runs Python for the live student previews, the same way the student pages do: the
// variable-setup starter, the student code and the hidden test harness are run together,
// then the harness's global `result` list is read back as per-test pass/fail. The order
// follows the page being previewed: /workspace (TerminalContext.runTestsPython) runs
// starter, code, harness; the /session/:id IDEs (VizmentalIDE.runTests / CodeStu) run code,
// starter, harness. Saved harnesses from before per-test isolation are upgraded first, as
// the student runners do, so one raising test fails only itself.
//
// Differences from the student path, all deliberate:
//   - nothing is saved (no code row, no submission, no telemetry);
//   - one Pyodide instance is loaded once and reused, but every run gets a fresh globals
//     dict, so runs are isolated from each other without re-downloading the runtime.

const PYODIDE_INDEX_URL = 'https://cdn.jsdelivr.net/pyodide/v0.23.4/full/'

// Preview-only safety net. Python runs on the page's main thread, so a student-style
// infinite loop (`while True:`) would freeze the whole wizard tab and lose everything not
// yet saved on the left. A trace hook stops a run after this many seconds instead. (Student
// runs have no such limit; this only protects the instructor's wizard.)
const PREVIEW_TIME_LIMIT_S = 10
const TIMEOUT_MARKER = 'VIZPI_PREVIEW_TIMEOUT'

// Runs __lp_source in the run's own globals (so the harness's `result` lands there, as
// before), with a line-trace deadline. Compiled as '<exec>' so tracebacks keep the same
// shape trimPythonError expects.
//
// CPython drops a trace hook the moment it raises. The test harness wraps each test in its
// own `except BaseException:`, so the first timeout would be swallowed and the next test
// would loop forever with no hook left. On timeout a profile hook is therefore installed
// too; it fires on the next call and re-arms tracing on every live frame of the student's
// code, so the timeout keeps firing until the run ends (every later test fails too). The
// wrapper's own frame is never traced (its cleanup must always run), and __lp_active turns
// both hooks into no-ops - the JS side clears it before resetting. Known gap: a loop that
// makes no calls at all inside a bare try/except can still escape.
const GUARDED_RUN = `
import sys as __lp_sys, time as __lp_time
__lp_top = __lp_sys._getframe()
__lp_end = __lp_time.monotonic() + __lp_limit
__lp_timed_out = False
__lp_active = True
def __lp_arm(frame, event, arg):
    if not __lp_active:
        __lp_sys.setprofile(None)
        return
    __lp_sys.settrace(__lp_trace)
    __lp_f = frame
    while __lp_f is not None and __lp_f is not __lp_top:
        __lp_f.f_trace = __lp_trace
        __lp_f = __lp_f.f_back
def __lp_trace(frame, event, arg):
    global __lp_timed_out
    if not __lp_active or frame is __lp_top:
        return None
    if __lp_time.monotonic() > __lp_end:
        __lp_timed_out = True
        __lp_sys.setprofile(__lp_arm)
        raise TimeoutError('${TIMEOUT_MARKER}')
    return __lp_trace
__lp_sys.settrace(__lp_trace)
try:
    exec(compile(__lp_source, '<exec>', 'exec'), globals())
finally:
    __lp_active = False
    __lp_sys.settrace(None)
    __lp_sys.setprofile(None)
`

// The guard's own exec() frame, as it appears in a traceback. Dropped from untrimmed errors
// so their frames match a direct runPythonAsync of the same source.
const GUARD_FRAME = `  File "<exec>", line ${GUARDED_RUN.split('\n').findIndex((line) => line.includes('exec(compile(')) + 1}, in <module>\n`

let pyodidePromise = null
const getPyodide = () => {
  if (!pyodidePromise) {
    pyodidePromise = loadPyodide({ indexURL: PYODIDE_INDEX_URL })
      .then(async (pyodide) => {
        // Same as the student IDE: exercises may `import numpy`.
        try {
          await pyodide.loadPackage('numpy')
        } catch (error) {
          console.warn('Preview: numpy failed to load', error)
        }
        return pyodide
      })
      .catch((error) => {
        pyodidePromise = null
        throw error
      })
  }
  return pyodidePromise
}

// The student IDE shows a Python error from the last '<exec>' / '<module>' frame on, so
// students see their own line rather than Pyodide's internals. Same trimming here.
export const trimPythonError = (raw) => {
  const text = String(raw || '')
  const indexE = text.lastIndexOf('<exec>')
  const indexM = text.lastIndexOf('<module>')
  const index = Math.max(indexE, indexM)
  if (index === -1) return text
  return index === indexE ? text.substring(index - 6) : text.substring(index + 9)
}

// Resolves to { stdout, error, results }:
//   stdout   everything printed (stdout and stderr, in order)
//   error    traceback string (trimmed unless trimError is false), or null
//   results  what the harness's `result` held ([] when the run never created it), or null
//            when withTests is false. Not padded: callers pad to their test list with
//            completeTestResults.
// order: 'ide' (code, starter, harness) or 'workspace' (starter, code, harness - joined
// exactly as TerminalContext does, even with tests off, so traceback line numbers match).
// trimError: false keeps Pyodide's whole message, as /workspace shows err.message.
// captureStderr: false leaves stderr (sys.stderr writes, warnings) out of the output, as
// /workspace's terminal never shows it; it goes to the browser console instead.
export const runPythonPreview = async ({
  code = '',
  starter = '',
  harness = '',
  withTests = true,
  order = 'ide',
  trimError = true,
  captureStderr = true,
}) => {
  const pyodide = await getPyodide()
  let stdout = ''
  const collect = (line) => {
    stdout += `${line}\n`
  }
  pyodide.setStdout({ batched: collect })
  pyodide.setStderr({ batched: captureStderr ? collect : (line) => console.warn(line) })

  // A fresh __main__ module per run (pyodideMain.js): runs are isolated from each other,
  // and `if __name__ == "__main__":` blocks, pickle and doctest behave as in the student's
  // own page.
  const run = freshMainGlobals(pyodide)
  const { globals } = run
  // input() opens the browser prompt, as in the student IDE. Time spent answering it is not
  // the program's fault, so it is added back to the deadline.
  pyodide.setStdin({
    stdin: () => {
      const waitStarted = Date.now()
      const answer = typeof window !== 'undefined' ? window.prompt('Input:') : null
      try {
        const end = globals.get('__lp_end')
        if (typeof end === 'number') globals.set('__lp_end', end + (Date.now() - waitStarted) / 1000)
      } catch (deadlineError) {
        // deadline not set yet
      }
      return answer
    },
  })
  const timeoutMessage =
    `Stopped: the program ran longer than ${PREVIEW_TIME_LIMIT_S} seconds in the preview (possibly an infinite loop).`
  let error = null
  let results = null
  try {
    const testHarness = withTests ? upgradeLegacyPythonHarness(harness) : ''
    let source
    if (order === 'workspace') source = [withTests ? starter : '', code, testHarness].join('\n')
    else source = withTests ? [code, starter, testHarness].join('\n') : code
    globals.set('__lp_source', source)
    globals.set('__lp_limit', PREVIEW_TIME_LIMIT_S)
    await pyodide.runPythonAsync(GUARDED_RUN, { globals })
  } catch (err) {
    const raw = String(err?.message || err)
    if (raw.includes(TIMEOUT_MARKER)) error = timeoutMessage
    else error = trimError ? trimPythonError(raw) : raw.replace(GUARD_FRAME, '')
  } finally {
    // In case the run ended some other way with a hook still set: disarm the hooks first
    // (from JS, which is not traced), then remove them.
    try {
      globals.set('__lp_active', false)
      pyodide.runPython('import sys\nsys.settrace(None)\nsys.setprofile(None)')
    } catch (resetError) {
      // nothing to reset
    }
  }
  // A harness that catches every exception swallows the timeout and finishes "normally";
  // still tell the instructor the run was cut short.
  if (!error) {
    try {
      if (globals.get('__lp_timed_out') === true) error = timeoutMessage
    } catch (flagError) {
      // flag not set
    }
  }
  if (withTests) results = readPythonResult(globals)
  run.release()
  return { stdout, error, results }
}
